import { useState, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';

// Our Components
import Dropdown from 'components/Dropdown/Dropdown';
import FinancialGoalDropdown from 'components/Dropdown/FinancialGoalDropdown';
import FinancialLiabilityForm from 'components/Forms/Liabilities/Common/FinancialLiabilityForm';
import FormButton from 'components/Forms/FormButton';
import LiabilityFormHeading from 'components/Forms/Liabilities/LiabilityFormHeading';
import LiabilityFormContainer from 'components/Forms/Liabilities/Common/LiabilityFormContainer';
import LiabilityFormItem from 'components/Forms/Liabilities/Common/LiabilityFormItem';
import Loader from 'components/Loader';
import MileageTextInput from 'components/Input/MileageTextInput';
import TextInput from 'components/Input/TextInput';
import USStateDropdown from 'components/Dropdown/USStateDropdown';

// Input Types
import { TEXT } from 'components/Input/Types';

// Our hooks
import useGetUserAutoLiability from 'hooks/client/liabilities/queries/useGetUserAutoLiability';
import useSaveAutoLiability from 'hooks/client/liabilities/mutations/useMutateSaveAutoLiability';
import usePrepopulateState from 'hooks/prepopulate/usePrepopulateState';

// Utils
import {
	dateToIsoFormat,
	FINANCIAL_GOAL_TO_ENUM_MAPPING,
	getDefaultApproxPayoffDate,
	isSubmissionReady,
	roundToDecimal
} from 'shared/utils';
import formatLoanType from 'shared/utils/formatting/liability/formatLoanType';

// CONSTANTS
import { AUTO } from 'shared/constants';

const VEHICLE_CONDITION_OPTIONS = ['Excellent', 'Good', 'Fair', 'Poor']; // This is for the DatePicker of Graduation Year

function AutoLiability() {
	const navigate = useNavigate();
	const saveUserAutoLiability = useSaveAutoLiability();

	const { isLoading, data, isSuccess } = useGetUserAutoLiability();

	const { isLoading: isMutationLoading } = saveUserAutoLiability;

	// Auto Liability Related
	const [approxPayoffDate, setApproxPayoffDate] = useState(
		getDefaultApproxPayoffDate(AUTO)
	);
	const [lender, setLender] = useState('');
	const [loanType, setLoanType] = useState('Fixed');
	const [monthlyPayment, setMonthlyPayment] = useState('');
	const [ownershipTenure, setOwnershipTenure] = useState('');
	const [outstandingBalance, setOutstandingBalance] = useState(''); // balance / 100 is bc methodfi provides balance in cents so we divide by 100 to convert into dollars
	const [rate, setRate] = useState('');

	// Vehicle Related Data
	const [licensePlate, setLicensePlate] = useState('');
	const [usState, setUSState] = useState('');
	const [vehicleCondition, setVehicleCondition] = useState('');
	const [vehicleMileage, setVehicleMileage] = useState('');
	const [vehicleVINNumber, setVehicleVINNumber] = useState('');

	// Financial Goal
	const [financialGoal, setFinancialGoal] = useState(
		'Reduce total cost of debt'
	);

	usePrepopulateState(
		isSuccess
			? [
					{
						setState: setOutstandingBalance,
						value: `${data?.outstandingBalance ?? ''}`
					},
					{ setState: setRate, value: `${data?.interestRate ?? ''}` },
					{
						setState: setApproxPayoffDate,
						value: data?.expectedPayOffDate
					},
					{ setState: setLender, value: data?.lender },
					{ setState: setMonthlyPayment, value: data?.monthlyPay },
					{
						setState: setOwnershipTenure,
						value: `${Math.floor(data?.tenureMonth ?? 0) / 12}`
					},
					{
						setState: setVehicleCondition,
						value: data?.vehicleInfo?.condition
					},
					{
						setState: setVehicleMileage,
						value: `${data?.vehicleInfo?.mileage ?? ''}`
					},
					{
						setState: setVehicleVINNumber,
						value: data?.vehicleInfo?.vin
					},
					{
						setState: setLicensePlate,
						value: data?.vehicleInfo?.licensePlate
					},
					{ setState: setUSState, value: data?.vehicleInfo?.state },
					{ setState: setFinancialGoal, value: data?.financialGoal },
					{
						setState: setLoanType,
						value: formatLoanType(data?.loanType ?? '')
					}
			  ]
			: [],
		[data]
	);

	const formValues = [
		approxPayoffDate,
		financialGoal,
		lender,
		monthlyPayment,
		outstandingBalance,
		ownershipTenure,
		loanType,
		rate,
		vehicleCondition,
		vehicleMileage
	];

	const isFormReady = useMemo(
		() => isSubmissionReady(formValues) && approxPayoffDate !== null,
		formValues
	);

	const handleApproxPayoffDateChange = (value) => {
		const isValueEmpty = value === '';
		if (isValueEmpty) {
			setApproxPayoffDate('');
			return;
		}
		setApproxPayoffDate(value);
	};

	const submitLiabilityData = (route) => {
		const formattedApproxPayOffDate = dateToIsoFormat(approxPayoffDate);
		const formattedFinancialGoal =
			FINANCIAL_GOAL_TO_ENUM_MAPPING(financialGoal);
		const formattedLoanType = loanType.toUpperCase();
		const formattedRate = roundToDecimal(+rate, 2);

		const autoData = {
			approxPayoffDate: formattedApproxPayOffDate,
			condition: vehicleCondition,
			financialGoal: formattedFinancialGoal,
			lender,
			licensePlate,
			loanType: formattedLoanType,
			mileage: vehicleMileage,
			monthlyPayment: +monthlyPayment,
			outstandingBalance: +outstandingBalance,
			rate: formattedRate,
			tradelineType: AUTO, // can be one of AUTO | MORTGAGE | PERSONAL | STUDENT
			tenure: +ownershipTenure,
			state: usState,
			vin: vehicleVINNumber
		};

		saveUserAutoLiability.mutate(autoData, {
			onSuccess: () => {
				navigate(route);
			}
		});
	};

	if (isLoading)
		return (
			<>
				<Helmet>
					<title>Auto Loan</title>
				</Helmet>
				<LiabilityFormHeading headingText="Auto Loan" />
				<Loader
					size={60}
					boxSX={{ alignItems: 'center', marginTop: 15 }}
				/>
			</>
		);

	return (
		<>
			<Helmet>
				<title>Auto Loan</title>
			</Helmet>

			{/* Once data binding begins we will add Mortgage specific information to this header */}
			<LiabilityFormHeading headingText="Auto Loan" />

			<LiabilityFormContainer>
				<FinancialLiabilityForm
					outstandingBalance={outstandingBalance}
					setOutstandingBalance={setOutstandingBalance}
					monthlyPayment={monthlyPayment}
					setMonthlyPayment={setMonthlyPayment}
					rate={rate}
					setRate={setRate}
					approxPayoffDate={approxPayoffDate}
					handleApproxPayoffDateChange={handleApproxPayoffDateChange}
					ownershipTenure={ownershipTenure}
					setOwnershipTenure={setOwnershipTenure}
					loanType={loanType}
					setLoanType={setLoanType}
					lender={lender}
					setLender={setLender}
				/>

				<LiabilityFormItem>
					<TextInput
						type={TEXT}
						label="Vehicle VIN number"
						subLabel="Optional"
						value={vehicleVINNumber}
						onChange={setVehicleVINNumber}
					/>
				</LiabilityFormItem>

				<LiabilityFormItem>
					<TextInput
						type={TEXT}
						label="License Plate number"
						subLabel="Optional"
						value={licensePlate}
						onChange={setLicensePlate}
					/>
				</LiabilityFormItem>

				<USStateDropdown
					state={usState}
					setState={setUSState}
					helperText="Optional"
				/>

				<LiabilityFormItem>
					<MileageTextInput
						vehicleMileage={vehicleMileage}
						setVehicleMileage={setVehicleMileage}
					/>
				</LiabilityFormItem>

				<LiabilityFormItem>
					<Dropdown
						dataTestTag="condition"
						items={VEHICLE_CONDITION_OPTIONS}
						selected={vehicleCondition}
						onChange={setVehicleCondition}
						variant="outlined"
						label="What condition is your vehicle in?"
					/>
				</LiabilityFormItem>

				<FinancialGoalDropdown
					withoutCashout
					financialGoal={financialGoal}
					setFinancialGoal={setFinancialGoal}
				/>

				<FormButton
					isFormReady={isFormReady}
					isMutationLoading={isMutationLoading}
					submitLiabilityData={submitLiabilityData}
				/>
			</LiabilityFormContainer>
		</>
	);
}

export default AutoLiability;
